import { BrandMark } from "./BrandMark";
import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Eye, EyeOff, ArrowLeft, ArrowUpRight, Mail } from "lucide-react";
type Mode = "signin" | "signup" | "forgot";
export function AccountAuth({
  getClient,
  email: initialEmail = "",
  purchase = false,
  recovery = false,
  computerApproval = false,
  onPasswordSaved,
}: {
  getClient: () => Promise<SupabaseClient>;
  email?: string;
  purchase?: boolean;
  recovery?: boolean;
  computerApproval?: boolean;
  onPasswordSaved: () => void;
}) {
  const [mode, setMode] = useState<Mode>(new URLSearchParams(location.search).get("onboarding")==="signup"?"signup":"signin"),
    [email, setEmail] = useState(initialEmail),
    [password, setPassword] = useState(""),
    [repeat, setRepeat] = useState(""),
    [visible, setVisible] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [sent, setSent] = useState(false);
  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);
  const setView = (next: Mode) => {
    setMode(next);
    setError("");
    setSent(false);
    setPassword("");
    setRepeat("");
  };
  async function submit() {
    setBusy(true);
    setError("");
    try {
      const client = await getClient();
      if (recovery) {
        if (password !== repeat)
          throw Error(
            "Passwords don’t match. Enter the same password in both fields.",
          );
        const result = await client.auth.updateUser({ password });
        if (result.error) throw result.error;
        // Revoke other browser sessions, preserving this newly secured session.
        const revoked = await client.auth.signOut({ scope: "others" });
        if (revoked.error)
          throw Error(
            "Password saved. Other sessions could not be signed out; try again.",
          );
        setPassword("");
        setRepeat("");
        history.replaceState(history.state, "", "/?view=projects");
        onPasswordSaved();
        return;
      }
      if (mode === "forgot") {
        const result = await client.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: location.origin + "/?view=password",
        });
        if (result.error) throw result.error;
        setSent(true);
      } else if (mode === "signup") {
        const result = await client.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: location.origin + "/" },
        });
        if (result.error) throw result.error;
        setPassword("");
        setSent(true);
      } else {
        const result = await client.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (result.error) throw result.error;
        setPassword("");
      }
    } catch (e) {
      const code = (e as { code?: string }).code;
      setError(
        !recovery && mode === "signin"
          ? code === "email_not_confirmed"
            ? "Verify your email before signing in. Check your inbox for the PhaseDrift verification email."
            : "Email or password wasn’t recognised. Try again, or choose Set or reset password."
          : code === "over_email_send_rate_limit"
            ? "Too many emails requested. Wait a minute, then try again."
            : e instanceof Error && e.message.startsWith("Passwords don’t")
              ? e.message
              : "We couldn’t complete that request. Try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }
  const title = recovery
    ? "Choose your password."
    : sent
      ? "Check your inbox."
      : mode === "signup"
        ? "Create your account."
        : mode === "forgot"
          ? "Set or reset your password."
          : computerApproval ? "Sign in to approve your computer." : "Welcome back.";
  return (
    <div className="auth-layout">
      <aside className="auth-story">
        <a className="history-brand" href="https://phasedrift.ai">
          <BrandMark />PhaseDrift
        </a>
        <div>
          <h2>
            A better environment.
            <br />
            Better agent work.
          </h2>
          <p>Tune the codebase your agents live and work in.</p>
          <div className="auth-path">
            <span>Work as usual</span>
            <span>Find the friction</span>
            <span>Improve the environment</span>
          </div>
        </div>
        <a
          href="https://phasedrift.ai/docs/get-set-up"
          target="_blank"
          rel="noopener noreferrer"
        >
          Explore the docs <ArrowUpRight size={16} />
        </a>
      </aside>
      <section className="auth-panel" aria-label="PhaseDrift sign in">
        <a
          className="auth-mobile-brand history-brand"
          href="https://phasedrift.ai"
        >
          <BrandMark />PhaseDrift
        </a>
        <div className="auth-form-wrap">
          {sent && (
            <div className="auth-mail-icon">
              <Mail size={26} />
            </div>
          )}
          <h1>{title}</h1>
          <p>
            {recovery
              ? "Use at least 12 characters. Your subscription and projects stay connected."
              : sent
                ? mode === "signup"
                  ? "Follow the verification link to finish creating your account."
                  : "If an account exists for this email, we’ve sent a private link to choose a password."
                : mode === "signup"
                  ? "Use your checkout email so your purchase connects automatically."
                  : mode === "forgot"
                    ? "Already used an email code? Use this to add a password to that same account."
                    : computerApproval
                      ? "Use your existing PhaseDrift account. You’ll check the terminal code before approving."
                    : purchase
                      ? "Sign in with your checkout email to connect your purchase."
                      : "Sign in to your PhaseDrift workspace."}
          </p>
          {!sent && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              aria-busy={busy}
            >
              {!recovery && (
                <label htmlFor="account-email">
                  Email
                  <input
                    id="account-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={busy}
                  />
                </label>
              )}
              {(recovery || mode !== "forgot") && (
                <label htmlFor="account-password">
                  {recovery ? "New password" : "Password"}
                  <div className="password-field">
                    <input
                      id="account-password"
                      aria-label={recovery ? "New password" : "Password"}
                      type={visible ? "text" : "password"}
                      autoComplete={
                        recovery || mode === "signup"
                          ? "new-password"
                          : "current-password"
                      }
                      minLength={recovery || mode === "signup" ? 12 : undefined}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={busy}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      aria-label={visible ? "Hide password" : "Show password"}
                      aria-pressed={visible}
                      onClick={() => setVisible(!visible)}
                    >
                      {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {mode === "signup" && <small>At least 12 characters.</small>}
                </label>
              )}
              {recovery && (
                <label htmlFor="repeat-password">
                  Confirm password
                  <input
                    id="repeat-password"
                    type={visible ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={12}
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    disabled={busy}
                  />
                </label>
              )}
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              <button className="primary-button" disabled={busy}>
                {busy
                  ? "Please wait…"
                  : recovery
                    ? "Save password"
                    : mode === "forgot"
                      ? "Send password link"
                      : mode === "signup"
                        ? "Create account"
                        : "Sign in"}
              </button>
            </form>
          )}
          {!recovery && (
            <div className="auth-links">
              {mode === "signin" ? (
                <>
                  <button
                    className="quiet-link"
                    onClick={() => setView("forgot")}
                  >
                    Set or reset password
                  </button>
                  <p>
                    New to PhaseDrift?{" "}
                    <button
                      className="quiet-link"
                      onClick={() => setView("signup")}
                    >
                      Create account
                    </button>
                  </p>
                </>
              ) : (
                <button
                  className="quiet-link"
                  onClick={() => setView("signin")}
                >
                  <ArrowLeft size={14} /> Back to sign in
                </button>
              )}
            </div>
          )}
          <p className="auth-help">
            Need a hand? <a href="mailto:hello@phasedrift.ai">Contact us</a>
          </p>
        </div>
      </section>
    </div>
  );
}
